import { useState, useEffect, useCallback } from "react";
import { supabaseAdmin as supabase } from "@/lib/supabaseAdmin";

interface AdminOverviewProps {
  onTabChange: (tab: string) => void;
}

interface OrderRow {
  id: string;
  user_id: string;
  product_name: string;
  amount: number;
  type: string;
  status: string;
  created_at: string;
}

interface WithdrawalRow {
  id: string;
  user_id: string;
  amount: number;
  status: string;
  type: string;
  bank_name: string | null;
  bank_account: string | null;
  created_at: string;
}

interface MonthlyRevenue {
  month: string;
  value: number;
}

const BASE_MEMBER_COUNT = 8420;
const MIN_DAILY_GROWTH = 100;
const MAX_DAILY_GROWTH = 150;

function getVirtualMemberCount(): number {
  const today = new Date().toISOString().slice(0, 10);
  const storedDate = localStorage.getItem("virtualMemberLastDate");
  const storedCount = localStorage.getItem("virtualMemberCount");

  let currentCount = storedCount ? parseInt(storedCount, 10) : BASE_MEMBER_COUNT;
  if (Number.isNaN(currentCount) || currentCount < BASE_MEMBER_COUNT) {
    currentCount = BASE_MEMBER_COUNT;
  }

  if (storedDate === today) {
    return currentCount;
  }

  const lastDate = storedDate ? new Date(storedDate) : new Date();
  const now = new Date(today);
  const dayDiff = Math.max(0, Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)));

  if (dayDiff > 0) {
    for (let i = 0; i < dayDiff; i++) {
      const increment = Math.floor(Math.random() * (MAX_DAILY_GROWTH - MIN_DAILY_GROWTH + 1)) + MIN_DAILY_GROWTH;
      currentCount += increment;
    }
    localStorage.setItem("virtualMemberCount", String(currentCount));
    localStorage.setItem("virtualMemberLastDate", today);
  } else if (!storedDate) {
    localStorage.setItem("virtualMemberCount", String(currentCount));
    localStorage.setItem("virtualMemberLastDate", today);
  }

  return currentCount;
}

export default function AdminOverview({ onTabChange }: AdminOverviewProps) {
  const [totalUsers, setTotalUsers] = useState(0);
  const [virtualMembers, setVirtualMembers] = useState(0);
  const [totalOrderAmount, setTotalOrderAmount] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [revenueTrend, setRevenueTrend] = useState<MonthlyRevenue[]>([]);
  const [loading, setLoading] = useState(true);

  // 穩定 fetchStats：useCallback + 空依賴，確保只執行一次
  const fetchStats = useCallback(async () => {
    setLoading(true);

    const [
      { count: userCount, error: userErr },
      { data: ordersData, error: ordersErr },
      { count: activeCount, error: activeErr },
      { count: pendingWdCount, error: wdErr },
    ] = await Promise.all([
      supabase.from("profiles").select("*", { count: "exact", head: true }),
      // 統計用：限制最近 500 筆即可，不需要全表
      supabase.from("orders").select("amount, created_at").limit(500),
      supabase.from("orders").select("*", { count: "exact", head: true }).eq("status", "active"),
      supabase.from("withdrawals").select("*", { count: "exact", head: true }).eq("status", "pending"),
    ]);

    if (userErr) console.error("profiles count error:", userErr);
    if (ordersErr) console.error("orders error:", ordersErr);
    if (activeErr) console.error("active orders error:", activeErr);
    if (wdErr) console.error("withdrawals error:", wdErr);

    setTotalUsers(userCount || 0);
    setVirtualMembers(getVirtualMemberCount());
    setActiveOrdersCount(activeCount || 0);
    setPendingWithdrawals(pendingWdCount || 0);

    if (ordersData) {
      setTotalSubscriptions(ordersData.length);
      const total = ordersData.reduce((sum, o) => sum + (o.amount || 0), 0);
      setTotalOrderAmount(total);

      // Group by month
      const monthlyMap = new Map<string, number>();
      ordersData.forEach((o: OrderRow) => {
        const month = o.created_at?.slice(0, 7) || "unknown";
        monthlyMap.set(month, (monthlyMap.get(month) || 0) + (o.amount || 0));
      });
      const trend = Array.from(monthlyMap.entries())
        .map(([month, value]) => ({ month, value }))
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6);
      setRevenueTrend(trend);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // 穩定 handleExportCSV
  const handleExportCSV = useCallback(async () => {
    const { data, error } = await supabase
      .from("orders")
      .select("id, user_id, product_name, amount, type, status, created_at")
      .order("created_at", { ascending: false })
      .limit(500);

    if (error || !data) {
      alert("匯出失敗：無法讀取訂單資料");
      return;
    }

    const headers = "訂單編號,會員ID,貨源名稱,認購金額,區域,狀態,日期";
    const rows = data.map((o: OrderRow) =>
      `${o.id},${o.user_id},${o.product_name},${o.amount},${o.type},${o.status},${o.created_at?.slice(0, 10) || ""}`
    );
    const csv = "\ufeff" + [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `認購報表_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const stats = [
    {
      icon: "ri-file-list-3-line",
      iconBg: "bg-cyan-50",
      iconColor: "text-cyan-600",
      label: "當前待辦",
      value: loading ? "—" : activeOrdersCount.toLocaleString(),
      sub: "進行中訂單",
      onClick: () => onTabChange("orders"),
    },
    {
      icon: "ri-money-dollar-circle-line",
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-600",
      label: "總認購金額",
      value: loading ? "—" : `NT$ ${totalOrderAmount.toLocaleString()}`,
      sub: "累計認購總額",
    },
    {
      icon: "ri-user-3-line",
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
      label: "註冊會員數",
      value: loading ? "—" : virtualMembers.toLocaleString(),
      sub: "已註冊會員",
    },
    {
      icon: "ri-time-line",
      iconBg: "bg-red-50",
      iconColor: "text-red-500",
      label: "待審核提現",
      value: loading ? "—" : pendingWithdrawals.toLocaleString(),
      sub: "需要處理",
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div
            key={i}
            onClick={s.onClick}
            className={`bg-white rounded-xl border border-navy-100 p-5 flex flex-col gap-3 ${s.onClick ? "cursor-pointer hover:border-navy-300 hover:bg-navy-50/50 transition-colors" : ""}`}
          >
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-lg ${s.iconBg} flex items-center justify-center`}>
                <i className={`${s.icon} ${s.iconColor} text-lg`} />
              </div>
            </div>
            <div>
              <p className="font-body text-xs text-navy-400 mb-1">{s.label}</p>
              <p className="font-display text-2xl font-bold text-navy-900">{s.value}</p>
              {s.sub && <p className="font-body text-[11px] text-navy-400 mt-0.5">{s.sub}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Trend */}
      <div className="bg-white rounded-xl border border-navy-100 p-5">
        <div className="mb-4">
          <h3 className="font-display text-sm font-bold text-navy-900">月度認購金額趨勢</h3>
          <p className="font-body text-[11px] text-navy-400 mt-0.5">近期各月認購總額（元）</p>
        </div>

        {loading ? (
          <p className="font-body text-sm text-navy-400 py-8 text-center">載入中...</p>
        ) : revenueTrend.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <div className="w-12 h-12 rounded-full bg-navy-50 flex items-center justify-center">
              <i className="ri-bar-chart-grouped-line text-navy-300 text-xl" />
            </div>
            <p className="font-body text-sm text-navy-400">系統數據統計中</p>
            <p className="font-body text-xs text-navy-300">尚無足夠歷史數據生成趨勢圖表</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-100">
                <th className="font-body text-[11px] font-semibold text-navy-600 text-left px-4 py-2">月份</th>
                <th className="font-body text-[11px] font-semibold text-navy-600 text-right px-4 py-2">認購金額</th>
                <th className="font-body text-[11px] font-semibold text-navy-600 text-right px-4 py-2">環比變化</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {revenueTrend.map((d, i) => {
                const prev = i > 0 ? revenueTrend[i - 1].value : d.value;
                const change = i > 0 && prev > 0 ? ((d.value - prev) / prev) * 100 : 0;
                return (
                  <tr key={d.month}>
                    <td className="px-4 py-2.5 font-body text-sm text-navy-900">{d.month}</td>
                    <td className="px-4 py-2.5 font-body text-sm text-navy-900 text-right">
                      NT$ {d.value.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 font-body text-xs text-right">
                      {i === 0 ? (
                        <span className="text-navy-400">—</span>
                      ) : (
                        <span className={change >= 0 ? "text-emerald-600" : "text-red-500"}>
                          {change >= 0 ? "+" : ""}{change.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick actions */}
      <div className="bg-white rounded-xl border border-navy-100 p-5">
        <h3 className="font-display text-sm font-bold text-navy-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: "ri-add-circle-line", label: "新增貨源", color: "text-navy-700", onClick: () => onTabChange("supply") },
            { icon: "ri-user-add-line", label: "新增會員", color: "text-emerald-600", onClick: () => onTabChange("users") },
            { icon: "ri-file-download-line", label: "匯出報表", color: "text-amber-600", onClick: handleExportCSV },
            { icon: "ri-settings-3-line", label: "系統設定", color: "text-navy-500", onClick: () => {} },
          ].map((a) => (
            <button
              key={a.label}
              onClick={a.onClick}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-navy-100 hover:border-navy-300 hover:bg-navy-50 cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${a.icon} ${a.color} text-xl`} />
              </div>
              <span className="font-body text-xs text-navy-600">{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}